import { useCallback, useEffect, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import './AdminClassroom.scss';
import images from '../../assets/images';
import TabsContentHeader from '../../components/AdminDashboardTabsContentHeader/TabsContentHeader';
import { GET_CLASSROOM_LIST } from '../AdminDashboard/api/AdminDashboardQuery';
import { useLazyQuery, useMutation } from '@apollo/client';
import AdminClassroomForm from './components/AdminClassroomForm';
import ActionsOverlay from '../../components/ActionsOverlay/ActionsOverlay';
import { CircularProgress, Menu, Tooltip } from '@mui/material';
import { DELETE_CLASSROOM, GET_CLASSROOM_BY_ID } from './api/AdminClassroomQuery';
import ToasterWithMessage from '../../components/toaster';
import ConfirmationPopup from '../../components/ConfirmationPopup/ConfirmationPopup';
import { debounce } from 'lodash';
import Loader from '../../components/Loader/Loader';
import { formatPupilsCount, getScreenDimensions } from '../../utils/helper';
import { FETCH_POLICY_NO_CACHE } from '../../utils/constants';

interface ClassroomByIdType {
    subjectBySjId: {
        sjName: string
        sjOriginalName: string;
    }
}
export interface ClassroomObjectType {
    cCode: string;
    cId: string;
    cName: string;
    cOriginalName: string;
    cStudentsCount: number;
    classroomSubjectsByCId: {
        nodes: ClassroomByIdType[]
    }
}
export interface ClassroomListType {
    allClassrooms: {
        nodes: ClassroomObjectType[];
        totalCount: number;
    }
}
interface Subjects {
    csAvatarUrl: string;
    subjectBySjId: {
        sjName: string;
        sjOriginalName?: string;
    }
}
interface TeacherForEdit {
    tId: string;
    teacherByTId: {
        tName: string;
    }
}
export interface ClassroomByIdDetailsType {
    cId: string;
    cCode: string;
    cName: string;
    cOriginalName: string;
    cStudentsCount: number;
    isShowVirtualClassroom: boolean;
    classroomSubjectsByCId: {
        nodes: Subjects[]
    };
    saId: string;
    teacherClassroomsByCId: {
        nodes: TeacherForEdit[]
    }
}

export interface AdminClassroomProps {
    isFullView: boolean;
    setisFullView: (value: boolean) => void;
    setisSearchClick: (value: boolean) => void;
    isSearchClick: boolean;
};

const AdminClassroom: React.FC<AdminClassroomProps> = ({isFullView,setisFullView,setisSearchClick,isSearchClick}) => {
    const { MoreIcon, bookIcon, DeleteGif, searchCloseIcon } = images;
    const [classroomsList, setAllClassroomsList] = useState<ClassroomListType>();
    const [hasMore, setHasMore] = useState(true);
    const [offset, setOffset] = useState(0);
    const limit = 50;
    const [displayForm, setDisplayForm] = useState<boolean>(false);
    const [openOverlay, setOpenOverlay] = useState<boolean>(false);
    const [actionData, setActionData] = useState<ClassroomObjectType>();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [refetchData, setRefetchData] = useState<boolean>(false);
    const [openConfirmationPopup, setOpenConfirmationPopup] = useState<boolean>(false);
    const [classroomByIdDetails, setClassroomByIdDetails] = useState<ClassroomByIdDetailsType>();
    const [clickAction, setClickAction] = useState("");
    const [searchValue, setSearchValue] = useState("");
    const { success, error } = ToasterWithMessage();
    const classNameRefs = useRef<(HTMLLabelElement | null)[]>([]);
    const [isOverflow, setIsOverflow] = useState<boolean[]>([]);
    const { isMobileScreen } = getScreenDimensions();

    const [getAllClassroom] = useLazyQuery(GET_CLASSROOM_LIST, {
        fetchPolicy: FETCH_POLICY_NO_CACHE,
        onCompleted: (data) => {
            setAllClassroomsList(prev => {
                if (!prev || offset === 0) {
                    return data;
                }
                // Merge new nodes with previous nodes for infinite scroll
                return {
                    ...data,
                    allClassrooms: {
                        ...data.allClassrooms,
                        nodes: [
                            ...(prev.allClassrooms?.nodes || []),
                            ...(data.allClassrooms?.nodes || [])
                        ]
                    }
                };
            });
            if (data?.allClassrooms?.nodes) {
                setHasMore(data.allClassrooms.nodes.length === limit && (offset + limit) < (data.allClassrooms.totalCount || 0));
            }
            setRefetchData(false);
        }
    });

    const [deleteClassroom] = useMutation(DELETE_CLASSROOM, {
        fetchPolicy: FETCH_POLICY_NO_CACHE,
        onCompleted: (data) => {
            success(data?.deleteClassroomById?.message);
            setRefetchData(true);
        },
        onError: (err) => {
            error(err?.message);
        }
    });

    const [getClassroomById] = useLazyQuery(GET_CLASSROOM_BY_ID, {
        fetchPolicy: FETCH_POLICY_NO_CACHE,
        onCompleted: (data) => {
            setDisplayForm(true);
            setisFullView(true);
            setClickAction('edit');
            setClassroomByIdDetails(data?.classroomByCId);
        }
    });

    useEffect(() => {
        setOffset(0);
        setHasMore(true);
        getAllClassroom({
            variables: {
                orderBy: ["C_NAME_ASC"],
                searchInput: '%%',
                limit,
                offset: 0
            }
        });
    }, []);

    useEffect(() => {
        if (displayForm === false) {
            setOffset(0);
            setHasMore(true);
            getAllClassroom({
                variables: {
                    orderBy: ["C_NAME_ASC"],
                    searchInput: `%${searchValue.trim()}%`,
                    limit,
                    offset: 0
                }
            });
        }
    }, [displayForm]);

    useEffect(() => {
        if (refetchData === true) {
            setOffset(0);
            setHasMore(true);
            getAllClassroom({
                variables: {
                    orderBy: ["C_NAME_ASC"],
                    searchInput: `%${searchValue.trim()}%`,
                    limit,
                    offset: 0
                }
            });
        }
    }, [refetchData]);

    //To search classroom
    const handleSearchDebounce = useCallback(
        debounce((value) => {
            setSearchValue(value);
            setOffset(0);
            if(value.trim() === ""){
                setAllClassroomsList(undefined);
            }
            if(value?.trim().length >= 3 || value.trim() === ""){
                getAllClassroom({
                    variables: {
                        orderBy: ["C_NAME_ASC"],
                        searchInput: `%${value.trim()}%`,
                        limit,
                        offset: 0
                    }
                });
            }
        }, 500),
        [getAllClassroom]
    );
    // Infinite scroll fetch more
    const fetchMoreClassrooms = () => {
        const newOffset = offset + limit;
        setOffset(newOffset);
        getAllClassroom({
            variables: {
                orderBy: ["C_NAME_ASC"],
                searchInput: `%${searchValue.trim()}%`,
                limit,
                offset: newOffset
            }
        });
    };

    //To open actions options
    const handleActions = (event: React.MouseEvent<HTMLElement>, data: ClassroomObjectType) => {
        event.stopPropagation(); // Stop propagation to prevent triggering parent click
        setAnchorEl(event.currentTarget);
        setActionData(data);
        setOpenOverlay(true);
    };

    //To close actions option
    const handleCloseActions = () => {
        setOpenOverlay(false);
    };

    // click yes button in confirmation popup
    const onYesButtonClick = () => {
        setOpenConfirmationPopup(false);
        deleteClassroom({
            variables: {
                input: {
                    classroomId: actionData?.cId
                }
            }
        });
    }

    // click no button in confirmation popup
    const onNoButtonClick = () => {
        setOpenConfirmationPopup(false);
    }

    //To get the classroom details by id
    const getClassroomDetails = (cId: string) => {
        if (openOverlay === false) {
            getClassroomById({
                variables: {
                    cId
                }
            });
        }
    }

    //To format classroom total count
    const formatClassroomCount = (count: number) => {
        if (count === 0) {
            return "No Classroom";
        } else if (count === 1) {
            return "1 Classroom";
        } else {
            return `${count} Classrooms`;
        }
    };
    
    //To check the classroomname is overflow
    const checkOverflow = (index: number) => {
        if (classNameRefs.current[index]) {
            const overflow = classNameRefs.current[index]!.scrollWidth > classNameRefs.current[index]!.clientWidth;
            setIsOverflow(prev => {
                const newOverflow = [...prev];
                newOverflow[index] = overflow;
                return newOverflow;
            });
        }
    };

    //To check overflow in resize 
    useEffect(() => {
        // Check overflow for all classrooms when the data is loaded
        if (classroomsList?.allClassrooms?.nodes) {
            classroomsList.allClassrooms.nodes.forEach((_, index) => {
                checkOverflow(index); // Check overflow for each classroom name
            });
        }
        const handleResize = () => {
            classroomsList?.allClassrooms?.nodes.forEach((_, index) => {
                checkOverflow(index);
            });
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [classroomsList]);

    const handleCloseSearch = () => {
        setisSearchClick(false);
        setSearchValue("");
        handleSearchDebounce("");
    };

    const searchOnChange = (event: { target: { value: string; }; }) => {
        setSearchValue(event.target.value);
        if (handleSearchDebounce){
          handleSearchDebounce(event?.target?.value);
        }
    };

    return (
        <>
            {
                !displayForm ?
                    <div className='AdminClassroom'>
                        {
                        (isMobileScreen && isSearchClick) ?
                        <div className={'adminClassroomSearch'}>
                                <input id="searchBox" type='text' placeholder='Search' onChange={searchOnChange} value={searchValue} className='SearchInputFieldStyle' />
                                {(getScreenDimensions()?.isMobileScreen && isSearchClick) && 
                                    <img src={searchCloseIcon} alt='closeIcon' className='closeIcon' onClick={handleCloseSearch} />
                                }
                        </div>
                        :
                        <TabsContentHeader
                            title={"All Classes"}
                            subtitle={formatClassroomCount(classroomsList?.allClassrooms?.totalCount || 0)}
                            addLabel={"Add Class"}
                            setClickAction={setClickAction}
                            setDisplayForm={setDisplayForm}
                            displayForm={displayForm}
                            setisFullView={setisFullView}
                            isFullView={isFullView}
                            subImage={bookIcon}
                            handleSearchDebounce={handleSearchDebounce}
                            searchValue={searchValue}
                            setSearchValue={setSearchValue}
                        />
                         }
                        {
                            (classroomsList === undefined) ? <Loader bottom='145px' />
                                :
                                (
                                    <InfiniteScroll
                                        dataLength={classroomsList?.allClassrooms?.nodes?.length || 0}
                                        next={fetchMoreClassrooms}
                                        hasMore={hasMore}
                                        loader={
                                            <div className="scroll-loader">
                                                <CircularProgress size={30} />
                                            </div>
                                        }
                                        height={`100%`}
                                        className={`${classroomsList?.allClassrooms?.totalCount ? 'classroomListContainer' : 'forNoDataFound'}`}
                                    >
                                        {
                                            classroomsList?.allClassrooms?.totalCount ?
                                                classroomsList?.allClassrooms?.nodes?.map((cardItem: ClassroomObjectType, index: number) => {
                                                    return (
                                                        <div className='classCardContainer' key={`${index}-${cardItem?.cId}`} onClick={() => getClassroomDetails(cardItem?.cId)}>
                                                            <div className='topSectionName'>
                                                                <Tooltip 
                                                                    title={<span>{cardItem?.cOriginalName}</span>}
                                                                    disableHoverListener={!isOverflow[index]}
                                                                    placement="top"
                                                                >
                                                                    <label 
                                                                        className='classRoomNameStyle' 
                                                                        ref={(el) => (classNameRefs.current[index] = el)} 
                                                                        onMouseEnter={() => checkOverflow(index)}
                                                                    >
                                                                        {cardItem?.cOriginalName}
                                                                    </label>
                                                                </Tooltip>
                                                                {isMobileScreen ? 
                                                                    <div className='noOfPeoples'>
                                                                        {formatPupilsCount(cardItem?.cStudentsCount || 0)} &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                                                                    </div>
                                                                    :
                                                                    <div className='action-btn' onClick={(event) => handleActions(event, cardItem)}>
                                                                        <img src={MoreIcon} alt="moreIcon" className='moreIconStyle' />
                                                                    </div>}
                                                            </div>
                                                            {isMobileScreen ? 
                                                                <div className='action-btn' onClick={(event) => handleActions(event, cardItem)}>
                                                                    <img src={MoreIcon} alt="moreIcon" className='moreIconStyle' />
                                                                </div>
                                                                :
                                                                <div className='noOfPeoples'>
                                                                    {formatPupilsCount(cardItem?.cStudentsCount || 0)} &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                                                                </div>
                                                            }
                                                        </div>
                                                    )
                                                })
                                                :
                                                <div className='noDataFoundStyle'>No data found</div>
                                        }
                                    </InfiniteScroll>
                                )
                        }

                        <Menu
                            id="action-menu"
                            MenuListProps={{
                                "aria-labelledby": "long-button",
                                disablePadding: true,
                            }}
                            anchorEl={anchorEl}
                            open={openOverlay}
                            onClose={handleCloseActions}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            sx={{
                                '& .MuiPaper-root': {
                                    borderRadius: '5px',
                                    boxShadow: '-10px 10px 15px 0.2px rgba(0, 0, 0, 0.05)',
                                },
                            }}
                        >
                            {actionData && 
                                <ActionsOverlay 
                                    handleCloseOverlay={handleCloseActions} 
                                    actionData={actionData} 
                                    setRefetchData={setRefetchData} 
                                    setOpenConfirmationPopup={setOpenConfirmationPopup} 
                                />
                            }
                        </Menu>
                        <ConfirmationPopup
                            open={openConfirmationPopup}
                            message={`Are you sure you want to Delete this classroom?`}
                            gifData={DeleteGif}
                            yesButtonFunctionality={onYesButtonClick}
                            noButtonFunctionality={onNoButtonClick}
                            messageWidth={'195px'}
                        />
                    </div> :
                    <>
                        <AdminClassroomForm
                            setDisplayForm={setDisplayForm}
                            displayForm={displayForm}
                            setisFullView={setisFullView}
                            isFullView={isFullView}
                            setClickAction={setClickAction}
                            clickAction={clickAction}
                            classroomByIdDetails={classroomByIdDetails}
                        />
                    </>
            }
        </>
    )
}
export default AdminClassroom;
